import React, { useState } from 'react';
import { useNotification, Notification } from '../../contexts/NotificationContext';
import './NotificationBell.css';
import Sheet from './Sheet';

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

const getNotificationIcon = (notification: Notification): string => {
  if (notification.type === 'user_created') return '👤';
  switch (notification.transaction_type) {
    case 'cash_withdrawal':
      return '💸';
    case 'cash_spending':
      return '🛒';
    case 'daily_closing':
      return '📊';
    default:
      return '📝';
  }
};

const NotificationBell: React.FC = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    pushPermission,
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush
  } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
  };

  const handlePushToggle = async () => {
    setIsSubscribing(true);
    try {
      if (isPushSubscribed) {
        await unsubscribeFromPush();
      } else {
        await subscribeToPush();
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="notification-bell">
      <button
        className="bell-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <Sheet title="Notifications" onClose={() => setIsOpen(false)}><div className="notification-dropdown">
          <div className="dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read"
                onClick={() => {
                  markAllAsRead();
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          {pushPermission !== 'unsupported' && (
            <div className="push-notification-toggle">
              <span className="push-label">
                {isPushSubscribed ? 'Push notifications enabled' : 'Enable push notifications'}
              </span>
              <button
                className={`push-toggle-btn ${isPushSubscribed ? 'enabled' : ''}`}
                onClick={handlePushToggle}
                disabled={isSubscribing || pushPermission === 'denied'}
                title={pushPermission === 'denied' ? 'Notifications blocked in browser settings' : ''}
              >
                {isSubscribing ? '...' : isPushSubscribed ? 'ON' : 'OFF'}
              </button>
            </div>
          )}

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                No notifications yet
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification); } }}
                  key={notification.notification_id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <span className="notification-icon">
                    {getNotificationIcon(notification)}
                  </span>
                  <div className="notification-content">
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">
                      {formatRelativeTime(notification.created_at)}
                    </span>
                  </div>
                  {!notification.is_read && <span className="unread-dot" />}
                </div>
              ))
            )}
          </div>
        </div></Sheet>
      )}
    </div>
  );
};

export default NotificationBell;
